import { useNavigate } from 'react-router';
import { Trans } from '@lingui/react/macro';
import { Mic } from 'lucide-react';

import AIAnalysis from '~/components/AIAnalysis';
import StickySidebar from '~/components/StickySidebar';
import { Button } from '~/components/ui/button';

export const handle = {
  title: 'Demo Analysis - Pavone',
  description: 'See how Pavone analyzes your speaking practice.',
};

// Static demo data showcasing the analysis features
const DEMO_ANALYSIS_DATA = {
  id: 'demo',
  transcription: `Hi, I'm Anya, and I've been in product management for about seven years, mostly in B2B SaaS. At my last company, I led the launch of an analytics dashboard that grew to over 50,000 users in the first year. Um, I think my biggest strength is being able to take complex problems and, you know, break them down into clear priorities. For example, when our team was deciding whether to build a new feature or optimize the existing funnel, I put together a data-driven case that helped us focus on optimization, which ended up increasing conversions by 18%. I'm really excited about this role because it combines strategic thinking with hands-on product work, which is exactly where I thrive.`,
  videoDurationSeconds: 75,
  status: 'completed',
  fillerWords: ['Um', 'you know'],
  score: 7,
  personalizedTips: [
    'Replace filler words like "um" and "you know" with brief pauses — silence sounds more confident',
    'Add a more specific hook in your opening — mention the target role or company to show preparation',
    'Quantify the analytics dashboard impact further — mention revenue or user engagement metrics',
  ],
  speechPace: 'Moderate',
  clarity: 'Clear',
  sentiment: 'Confident',
  keyPointsDetected: [
    '7 years of product management experience in B2B SaaS',
    'Led analytics dashboard launch reaching 50K users',
    'Data-driven decision making increased conversions by 18%',
  ],
  intentCategory: 'interview',
  intentDescription: 'Job interview practice',
  improvedScript: `Hi, I'm Anya. I've spent seven years in product management, specializing in B2B SaaS analytics.

At my last company, I led the launch of an analytics dashboard that grew to over 50,000 users in its first year, driving a 25% increase in customer retention.

My core strength is turning complex problems into clear priorities. When our team faced a build-versus-optimize decision, I built a data-driven case for optimization that increased conversions by 18% in one quarter.

I'm particularly excited about this role because it sits at the intersection of strategic thinking and hands-on product execution — that's where I do my best work.`,
  questionFeedback: [
    {
      question: 'Tell me about yourself and your experience.',
      strengths: [
        'Opened with name and role, establishing immediate context',
        'Quantified experience with specific years and domain',
        'Mentioned a key achievement with a concrete metric (50K users)',
      ],
      weaknesses: [
        'Opening could reference the specific role or company being interviewed for',
      ],
      suggestion: 'Tailor your opening line to connect your background directly to what this role needs — e.g., "I\'m excited to bring my 7 years of B2B SaaS experience to your product team."',
    },
    {
      question: 'Can you describe a time you had to make a difficult decision?',
      strengths: [
        'Used a real example with clear context about the build-vs-optimize dilemma',
        'Showed data-driven thinking and leadership',
        'Included a measurable result (18% conversion increase)',
      ],
      weaknesses: [
        'Could describe the stakes more vividly — what would have happened if the wrong choice was made?',
      ],
      suggestion: 'Add one sentence about the risk or pressure involved to make the story more compelling — e.g., "The team was split 50/50 and we had only one quarter to show results."',
    },
    {
      question: 'Why are you interested in this role?',
      strengths: [
        'Connected personal strengths to the role requirements',
        'Showed genuine enthusiasm and self-awareness',
      ],
      weaknesses: [
        'Closing was slightly generic — could reference something specific about the company',
      ],
      suggestion: 'Research one specific company initiative or product and explain why it excites you — this shows preparation and genuine interest.',
    },
  ],
  files: [],
  // Visual setting analysis data for demo
  visualSettingAnalyzedAt: new Date(),
  visualSettingGoodPoints: [
    'Good lighting on your face',
    'Clean and professional background',
  ],
  visualSettingBadPoints: [
    'Camera angle could be slightly higher for better framing',
  ],
  visualSettingSuggestions: [
    'Position camera at eye level for more engaging eye contact',
    'Consider adding a subtle backlight to separate from background',
  ],
  // Audio analysis data for demo
  audioAnalyzedAt: new Date(),
  audioPitchMean: 165.0,
  audioPitchVariation: 28.5,
  audioVolumeLevel: 'medium',
  audioToneQuality: 'warm',
  audioSpeakingRate: 142.0,
  audioEnergyLevel: 'high',
  audioAnalysis: null,
};

export default function Demo() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <a href="https://pavone.ai" className="block">
            <img src="/images/pavone-logo-v2.png" alt="Pavone" className="h-8" />
          </a>
          {/* <Button
            onClick={() => navigate('/sign-up')}
            className="bg-brand-dark-blue hover:bg-brand-dark-blue/90 text-white flex items-center gap-2"
          >
            <Mic className="w-4 h-4" />
            <Trans>Practice</Trans>
          </Button> */}
        </div>

        {/* Demo banner */}
        <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-lg p-4 mb-6">
          <p className="text-sm text-brand-dark-blue font-medium text-center">
            <Trans>This is a demo showing how Pavone analyzes your speaking practice</Trans>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
          {/* Analysis content */}
          <div className="space-y-6 pb-24 lg:pb-6">
            <AIAnalysis
              videoBlob={null}
              analysisData={DEMO_ANALYSIS_DATA}
              isAnalyzing={false}
              videoUrl="/videos/welcome.mp4"
              layout="data-first"
              thumbnailKey={null}
              primaryFileId={undefined}
              readOnly={true}
              demoThumbnailUrl="/images/welcome_eva.webp"
              reportAccess="full"
            />
          </div>

          {/* Sidebar */}
          <aside className="hidden lg:block">
            <StickySidebar offsetTop={24} analysisData={DEMO_ANALYSIS_DATA} isAnalyzing={false}>
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/sign-up')}
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2 bg-white transition-colors hover:bg-brand-dark-blue hover:text-white border-black/40 shadow-lg"
                >
                  <Mic className="w-4 h-4" />
                  <Trans>Practice</Trans>
                </Button>
              </div>
            </StickySidebar>
          </aside>
        </div>

        {/* CTA footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <p className="text-sm text-gray-600 hidden sm:block">
              <Trans>Want to improve your speaking skills?</Trans>
            </p>
            <Button
              onClick={() => navigate('/sign-up')}
              className="bg-brand-dark-blue hover:bg-brand-dark-blue/90 text-white flex items-center gap-2 w-full sm:w-auto"
            >
              <Mic className="w-4 h-4" />
              <Trans>Try Pavone for Free</Trans>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
